"use client";

import React from "react";
import { m as motion } from "framer-motion";
import {
  Search,
  Code,
  Zap,
  GitBranch,
  Palette,
  Terminal,
  Database,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { SEO_CONFIG } from "@/lib/seo-config";

interface EnhancedBlogListHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedCategory: string | null;
  onCategoryChange: (category: string | null) => void;
  categories: string[];
}

export const EnhancedBlogListHeader: React.FC<EnhancedBlogListHeaderProps> = ({
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  categories,
}) => {
  return (
    <div className="relative w-full pt-12 pb-8 overflow-hidden border-b border-border/40 mb-12">
      {/* Floating Developer Icons */}
      <motion.div
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-8 left-12 opacity-10 text-primary pointer-events-none"
      >
        <Code className="w-8 h-8" />
      </motion.div>
      <motion.div
        animate={{ y: [0, 20, 0] }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5,
        }}
        className="absolute top-20 right-20 opacity-10 text-primary pointer-events-none"
      >
        <Zap className="w-10 h-10" />
      </motion.div>
      <motion.div
        animate={{ y: [0, -15, 0] }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        className="absolute bottom-12 left-1/4 opacity-10 text-primary pointer-events-none"
      >
        <GitBranch className="w-9 h-9" />
      </motion.div>
      <motion.div
        animate={{ y: [0, 20, 0] }}
        transition={{
          duration: 4.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.5,
        }}
        className="absolute top-16 right-1/3 opacity-10 text-primary pointer-events-none"
      >
        <Palette className="w-7 h-7" />
      </motion.div>
      <motion.div
        animate={{ y: [0, -18, 0] }}
        transition={{
          duration: 5.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.8,
        }}
        className="absolute bottom-20 right-1/4 opacity-10 text-primary pointer-events-none"
      >
        <Terminal className="w-8 h-8" />
      </motion.div>
      <motion.div
        animate={{ y: [0, 15, 0] }}
        transition={{
          duration: 6.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.2,
        }}
        className="absolute top-24 left-1/3 opacity-10 text-primary pointer-events-none"
      >
        <Database className="w-9 h-9" />
      </motion.div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col gap-10">
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-2"
            >
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
                {SEO_CONFIG.blogName}
              </h1>
              <p className="text-base md:text-lg text-muted-foreground font-medium max-w-2xl leading-relaxed">
                Reflections on engineering, product design, and the architecture
                of the web.
              </p>
            </motion.div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="relative w-full md:w-80 group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search stories..."
                className="w-full pl-10 pr-4 py-2.5 bg-muted/50 border border-border/50 rounded-full text-sm outline-none focus:bg-background focus:border-primary/30 transition-all font-medium"
              />
            </div>
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide pb-2 md:pb-0">
              {categories.slice(0, 6).map((category) => {
                const isActive =
                  selectedCategory === category ||
                  (category === "All" && !selectedCategory);
                return (
                  <button
                    key={category}
                    onClick={() =>
                      onCategoryChange(category === "All" ? null : category)
                    }
                    className={cn(
                      "whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-bold transition-all",
                      isActive
                        ? "bg-foreground text-background shadow-sm"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    )}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedBlogListHeader;
